// utils/api.js - Servicio de API para Mi App Windows

import axios from 'axios';
import biometricoAPI from '../services/biometricoAPI';
// Configuración base de axios - La URL se carga desde api_url.txt vía Electron
let API_URL = '';

let impresorasNecesariasCache = [];
let ultimaConsultaEndpoint = null;
const tiempoCacheEndpoint = 5 * 60 * 1000; // 5 minutos en ms
let consultaEnProceso = false;
// Instancia de axios con configuración común
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Cargar la URL del API desde api_url.txt (vía Electron)
// Se guarda la promesa para que otros puedan esperar a que termine
let _apiUrlReady;
const apiUrlPromise = new Promise((resolve) => { _apiUrlReady = resolve; });

(async function initApiUrl() {
  try {
    if (window.electron && window.electron.getApiUrl) {
      const url = await window.electron.getApiUrl();
      if (url) {
        API_URL = url;
        apiClient.defaults.baseURL = url;
        console.log('API URL configurada desde api_url.txt:', url);
      } else {
        console.warn('No se pudo leer api_url.txt, usando URL por defecto.');
      }
    }
  } catch (err) {
    console.warn('No se pudo leer api_url.txt, usando URL por defecto:', err.message);
  }
  _apiUrlReady();
})();

// Esperar a que la URL esté lista (para componentes que necesitan la URL al iniciar)
export function waitForApiUrl() {
  return apiUrlPromise.then(() => API_URL);
}

// Getter síncrono para la URL actual
export function getApiUrl() {
  return API_URL;
}

// Interceptor para manejar errores comunes
apiClient.interceptors.response.use(
  response => response,
  error => {
    // Manejo centralizado de errores
    console.error('Error en la petición API:', error);

    if (error.response) {
      // Error con respuesta del servidor
      console.error('Respuesta del servidor:', error.response.data);

      // Si es error de autenticación, redirigir al login
      if (error.response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        localStorage.removeItem('systemConfig');
        localStorage.removeItem('appConfig');
        // Si estamos usando React Router, podríamos hacer una redirección aquí
      }
    }

    return Promise.reject(error);
  }
);

// Funciones auxiliares
const setAuthToken = (token) => {
  if (token) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('authToken', token);
  } else {
    delete apiClient.defaults.headers.common['Authorization'];
    localStorage.removeItem('authToken');
  }
};

// Inicializar token desde localStorage si existe
const token = localStorage.getItem('authToken');
if (token) {
  setAuthToken(token);
}

// Servicios de API
const API = {
  // Autenticación
  auth: {
    /**
     * Iniciar sesión
     * @param {string} usuario - Nombre de usuario
     * @param {string} contrasena - Contraseña del usuario
     * @param {Object|boolean} options - Opciones adicionales o flag biometricAuth para compatibilidad
     * @returns {Promise} - Promesa con los datos del usuario y token
     */
    login: async (usuario, contrasena, options = {}) => {
      try {
        // Compatibilidad con el parámetro boolean anterior
        const biometricAuth = typeof options === 'object' ? options.biometricAuth : options;

        const requestData = {
          tipo: 'login',
          usuario,
          contrasena
        };

        if (biometricAuth) {
          requestData.biometricAuth = true;
        }

        const response = await apiClient.post('', requestData);

        if (response.data.success) {
          // Guardar token y configurar interceptor
          if (response.data.data.token) {
            setAuthToken(response.data.data.token);
          }

          // Guardar datos del usuario
          localStorage.setItem('userData', JSON.stringify(response.data.data.usuario));

          // Guardar datos de system y configuracion
          if (response.data.data.system) {
            localStorage.setItem('systemConfig', JSON.stringify(response.data.data.system));
          }

          if (response.data.data.configuracion) {
            localStorage.setItem('appConfig', JSON.stringify(response.data.data.configuracion));
          }

          return response.data;
        } else {
          throw new Error(response.data.message || 'Error en la autenticación');
        }
      } catch (error) {
        throw error;
      }
    },

    /**
     * Cerrar sesión
     */
    logout: () => {
      // Eliminar token y datos de usuario
      setAuthToken(null);
      localStorage.removeItem('userData');
      localStorage.removeItem('systemConfig');
      localStorage.removeItem('appConfig');
    },

    /**
     * Verificar si el usuario actual tiene un permiso específico
     * @param {string} permiso - Nombre del permiso a verificar
     * @returns {boolean} - true si tiene el permiso, false en caso contrario
     */
    hasPermission: (permiso) => {
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');

      // Verificar si el usuario tiene el perfil y el permiso específico
      if (userData) {
        return userData[permiso] === '1' || userData[permiso] === 1;
      }

      return false;
    },

    /**
     * Obtener los datos del usuario actual
     * @returns {Object|null} - Datos del usuario o null si no hay usuario autenticado
     */
    getCurrentUser: () => {
      const userData = localStorage.getItem('userData');
      return userData ? JSON.parse(userData) : null;
    },

    /**
     * Obtener la configuración del sistema
     * @returns {Object|null} - Datos de configuración del sistema o null
     */
    getSystemConfig: () => {
      const systemConfig = localStorage.getItem('systemConfig');
      return systemConfig ? JSON.parse(systemConfig) : null;
    },

    /**
     * Obtener la configuración de la aplicación
     * @returns {Object|null} - Datos de configuración de la aplicación o null
     */
    getAppConfig: () => {
      const appConfig = localStorage.getItem('appConfig');
      return appConfig ? JSON.parse(appConfig) : null;
    }
  },

  // Módulos
  modulos: {
    /**
     * Obtener todos los módulos
     * @returns {Promise} - Promesa con la lista de módulos
     */
    getAll: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getModulos'
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener módulos');
        }
      } catch (error) {
        throw error;
      }
    },

    /**
     * Registrar o actualizar módulo actual
     * @param {string} ip - Dirección IP del equipo
     * @param {string} equipo - Nombre del equipo
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    registrarModulo: async (ip, equipo) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'registrarModulo',
          ip,
          equipo,
          ejecucion: 'main'
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al registrar módulo');
        }
      } catch (error) {
        throw error;
      }
    }
  },

  // Perfiles y permisos
  perfiles: {
    /**
     * Obtener todos los perfiles
     * @returns {Promise} - Promesa con la lista de perfiles
     */
    getAll: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPerfiles'
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener perfiles');
        }
      } catch (error) {
        throw error;
      }
    },

    /**
     * Obtener un perfil por ID
     * @param {number} idPerfil - ID del perfil a obtener
     * @returns {Promise} - Promesa con los datos del perfil
     */
    getById: async (idPerfil) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPerfil',
          idPerfil
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener perfil');
        }
      } catch (error) {
        throw error;
      }
    }
  },

  // Mesas
  mesas: {
    /**
     * Obtener todas las mesas
     * @returns {Promise} - Promesa con la lista de mesas
     */
    getAll: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getMesas'
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener mesas');
        }
      } catch (error) {
        console.error('Error al obtener mesas:', error);
        throw error;
      }
    },

    /**
     * Obtener una mesa por ID
     * @param {number} idMesa - ID de la mesa a obtener
     * @returns {Promise} - Promesa con los datos de la mesa
     */
    getById: async (idMesa) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getMesa',
          idMesa
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener la mesa');
        }
      } catch (error) {
        console.error(`Error al obtener mesa con ID ${idMesa}:`, error);
        throw error;
      }
    },

    /**
     * Obtener mesas activas (con pedidos abiertos)
     * @returns {Promise} - Promesa con la lista de mesas activas
     */
    getActivas: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getMesasActivas'
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener mesas activas');
        }
      } catch (error) {
        console.error('Error al obtener mesas activas:', error);
        throw error;
      }
    },

    /**
     * Actualizar el estado de una mesa
     * @param {number} idMesa - ID de la mesa a actualizar
     * @param {string} status - Nuevo estado ('abierta', 'ocupada', etc.)
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    updateStatus: async (idMesa, status) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'updateMesaStatus',
          idMesa,
          status
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar el estado de la mesa');
        }
      } catch (error) {
        console.error(`Error al actualizar el estado de la mesa ${idMesa}:`, error);
        throw error;
      }
    }
  },

  // Funciones de utilidad
  utils: {
    /**
     * Obtener información del sistema
     * @returns {Promise} - Promesa con la información del sistema
     */
    getSystemInfo: async () => {
      try {
        const response = await apiClient.get('');

        if (response.data.success) {
          return response.data.systemInfo;
        } else {
          throw new Error(response.data.message || 'Error al obtener información del sistema');
        }
      } catch (error) {
        throw error;
      }
    },

    /**
     * Actualizar configuración del sistema en localStorage
     * @param {Object} systemConfig - Nueva configuración del sistema
     */
    updateSystemConfig: (systemConfig) => {
      localStorage.setItem('systemConfig', JSON.stringify(systemConfig));
    },

    /**
     * Actualizar configuración de la aplicación en localStorage
     * @param {Object} appConfig - Nueva configuración de la aplicación
     */
    updateAppConfig: (appConfig) => {
      localStorage.setItem('appConfig', JSON.stringify(appConfig));
    }
  },

  // Pedidos
  pedidos: {

    // Additions to your existing api.js file for handling Uber, Rappi, and Delibot orders

    // UBER ORDERS
    /**
     * Create a new Uber order
     * @param {Object} datosPedido - Data for the new Uber order
     * @returns {Promise} - Promise with the server response
     */
    crearPedidoUber: async (datosPedido) => {
      try {
        // Asegurarse de que el tipo de servicio sea 'uber'
        const pedidoData = {
          ...datosPedido,
          tipo: datosPedido.tipoServicio || 'uber',
          estado: 'abierto',
          fechaCreacion: new Date().toISOString()
        };

        const response = await apiClient.post('', {
          tipo: 'crearPedidoUber',
          datosPedido: pedidoData
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al crear pedido Uber');
        }
      } catch (error) {
        console.error('Error al crear pedido Uber:', error);
        throw error;
      }
    },

    /**
     * Update an existing Uber order
     * @param {Object} datosPedido - Data for updating the Uber order
     * @returns {Promise} - Promise with the server response
     */
    actualizarPedidoUber: async (datosPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarPedidoUber',
          datosPedido: datosPedido
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar pedido Uber');
        }
      } catch (error) {
        console.error('Error al actualizar pedido Uber:', error);
        throw error;
      }
    },

    /**
     * Add a product to an Uber order
     * @param {number} idPedido - Order ID
     * @param {Object} producto - Product to add
     * @returns {Promise} - Promise with the server response
     */
    agregarProductoPedidoUber: async (idPedido, producto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'agregarProductoPedidoUber',
          idPedido: idPedido,
          producto: producto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al agregar producto al pedido Uber');
        }
      } catch (error) {
        console.error('Error al agregar producto al pedido Uber:', error);
        throw error;
      }
    },

    /**
     * Get an Uber order by ID
     * @param {number} idPedido - Order ID
     * @returns {Promise} - Promise with the server response
     */
    getPedidoUber: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidoUber',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener pedido Uber');
        }
      } catch (error) {
        console.error('Error al obtener pedido Uber:', error);
        return null;
      }
    },

    /**
     * Get Uber orders by status
     * @param {string} estado - Order status (default: 'abiertos')
     * @returns {Promise} - Promise with the server response
     */
    getPedidosUber: async (estado = 'abiertos') => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidosUber',
          estado: estado
        });

        if (response.data.success) {
          return response.data.data || [];
        } else {
          throw new Error(response.data.message || 'Error al obtener pedidos Uber');
        }
      } catch (error) {
        console.error('Error al obtener pedidos Uber:', error);
        return [];
      }
    },

    /**
     * Finalize an Uber order
     * @param {number} idPedido - Order ID
     * @returns {Promise} - Promise with the server response
     */
    cerrarPedidoUber: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cerrarPedidoUber',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al cerrar pedido Uber');
        }
      } catch (error) {
        console.error('Error al cerrar pedido Uber:', error);
        throw error;
      }
    },

    finalizarPedidoUber: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'finalizarPedidoUber',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al finalizar pedido Uber');
        }
      } catch (error) {
        console.error('Error al finalizar pedido Uber:', error);
        throw error;
      }
    },

    abrirCuentaUber: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'abrirCuenta',  // Usar el tipo genérico que ya existe en el backend
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al abrir cuenta Uber');
        }
      } catch (error) {
        console.error('Error al abrir cuenta Uber:', error);
        throw error;
      }
    },

    // RAPPI ORDERS
    /**
     * Create a new Rappi order
     * @param {Object} datosPedido - Data for the new Rappi order
     * @returns {Promise} - Promise with the server response
     */
    crearPedidoRappi: async (datosPedido) => {
      try {
        // Asegurarse de que el tipo de servicio sea 'rappi'
        const pedidoData = {
          ...datosPedido,
          tipo: datosPedido.tipoServicio || 'rappi',
          estado: 'abierto',
          fechaCreacion: new Date().toISOString()
        };

        const response = await apiClient.post('', {
          tipo: 'crearPedidoRappi',
          datosPedido: pedidoData
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al crear pedido Rappi');
        }
      } catch (error) {
        console.error('Error al crear pedido Rappi:', error);
        throw error;
      }
    },

    /**
     * Update an existing Rappi order
     * @param {Object} datosPedido - Data for updating the Rappi order
     * @returns {Promise} - Promise with the server response
     */
    actualizarPedidoRappi: async (datosPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarPedidoRappi',
          datosPedido: datosPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar pedido Rappi');
        }
      } catch (error) {
        console.error('Error al actualizar pedido Rappi:', error);
        throw error;
      }
    },

    /**
     * Add a product to a Rappi order
     * @param {number} idPedido - Order ID
     * @param {Object} producto - Product to add
     * @returns {Promise} - Promise with the server response
     */
    agregarProductoPedidoRappi: async (idPedido, producto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'agregarProductoPedidoRappi',
          idPedido: idPedido,
          producto: producto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al agregar producto al pedido Rappi');
        }
      } catch (error) {
        console.error('Error al agregar producto al pedido Rappi:', error);
        throw error;
      }
    },

    /**
     * Get a Rappi order by ID
     * @param {number} idPedido - Order ID
     * @returns {Promise} - Promise with the server response
     */
    getPedidoRappi: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidoRappi',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener pedido Rappi');
        }
      } catch (error) {
        console.error('Error al obtener pedido Rappi:', error);
        return null;
      }
    },

    /**
     * Get Rappi orders by status
     * @param {string} estado - Order status (default: 'abiertos')
     * @returns {Promise} - Promise with the server response
     */
    getPedidosRappi: async (estado = 'abiertos') => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidosRappi',
          estado: estado
        });

        if (response.data.success) {
          return response.data.data || [];
        } else {
          throw new Error(response.data.message || 'Error al obtener pedidos Rappi');
        }
      } catch (error) {
        console.error('Error al obtener pedidos Rappi:', error);
        return [];
      }
    },

    /**
     * Finalize a Rappi order
     * @param {number} idPedido - Order ID
     * @returns {Promise} - Promise with the server response
     */
    cerrarPedidoRappi: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cerrarPedidoRappi',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al cerrar pedido Rappi');
        }
      } catch (error) {
        console.error('Error al cerrar pedido Rappi:', error);
        throw error;
      }
    },

    finalizarPedidoRappi: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'finalizarPedidoRappi',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al finalizar pedido Rappi');
        }
      } catch (error) {
        console.error('Error al finalizar pedido Rappi:', error);
        throw error;
      }
    },

    abrirCuentaRappi: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'abrirCuenta',  // Usar el tipo genérico que ya existe en el backend
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al abrir cuenta Rappi');
        }
      } catch (error) {
        console.error('Error al abrir cuenta Rappi:', error);
        throw error;
      }
    },

    // DELIBOT ORDERS
    /**
     * Create a new Delibot order
     * @param {Object} datosPedido - Data for the new Delibot order
     * @returns {Promise} - Promise with the server response
     */
    crearPedidoDelibot: async (datosPedido) => {
      try {
        // Asegurarse de que el tipo de servicio sea 'delibot'
        const pedidoData = {
          ...datosPedido,
          tipo: datosPedido.tipoServicio || 'delibot',
          estado: 'abierto',
          fechaCreacion: new Date().toISOString()
        };

        const response = await apiClient.post('', {
          tipo: 'crearPedidoDelibot',
          datosPedido: pedidoData
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al crear pedido Delibot');
        }
      } catch (error) {
        console.error('Error al crear pedido Delibot:', error);
        throw error;
      }
    },

    /**
     * Update an existing Delibot order
     * @param {Object} datosPedido - Data for updating the Delibot order
     * @returns {Promise} - Promise with the server response
     */
    actualizarPedidoDelibot: async (datosPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarPedidoDelibot',
          datosPedido: datosPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar pedido Delibot');
        }
      } catch (error) {
        console.error('Error al actualizar pedido Delibot:', error);
        throw error;
      }
    },

    /**
     * Add a product to a Delibot order
     * @param {number} idPedido - Order ID
     * @param {Object} producto - Product to add
     * @returns {Promise} - Promise with the server response
     */
    agregarProductoPedidoDelibot: async (idPedido, producto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'agregarProductoPedidoDelibot',
          idPedido: idPedido,
          producto: producto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al agregar producto al pedido Delibot');
        }
      } catch (error) {
        console.error('Error al agregar producto al pedido Delibot:', error);
        throw error;
      }
    },

    /**
     * Get a Delibot order by ID
     * @param {number} idPedido - Order ID
     * @returns {Promise} - Promise with the server response
     */
    getPedidoDelibot: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidoDelibot',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener pedido Delibot');
        }
      } catch (error) {
        console.error('Error al obtener pedido Delibot:', error);
        return null;
      }
    },

    /**
     * Get Delibot orders by status
     * @param {string} estado - Order status (default: 'abiertos')
     * @returns {Promise} - Promise with the server response
     */
    getPedidosDelibot: async (estado = 'abiertos') => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidosDelibot',
          estado: estado
        });

        if (response.data.success) {
          return response.data.data || [];
        } else {
          throw new Error(response.data.message || 'Error al obtener pedidos Delibot');
        }
      } catch (error) {
        console.error('Error al obtener pedidos Delibot:', error);
        return [];
      }
    },

    /**
     * Finalize a Delibot order
     * @param {number} idPedido - Order ID
     * @returns {Promise} - Promise with the server response
     */
    cerrarPedidoDelibot: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cerrarPedidoDelibot',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al cerrar pedido Delibot');
        }
      } catch (error) {
        console.error('Error al cerrar pedido Delibot:', error);
        throw error;
      }
    },

    finalizarPedidoDelibot: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'finalizarPedidoDelibot',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al finalizar pedido Delibot');
        }
      } catch (error) {
        console.error('Error al finalizar pedido Delibot:', error);
        throw error;
      }
    },

    abrirCuentaDelibot: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'abrirCuenta',  // Usar el tipo genérico que ya existe en el backend
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al abrir cuenta Delibot');
        }
      } catch (error) {
        console.error('Error al abrir cuenta Delibot:', error);
        throw error;
      }
    },

    // Añadir esta función al objeto API.pedidos en el archivo api.js

    /**
     * Crear un nuevo pedido para llevar
     * @param {Object} datosPedido - Datos del pedido para llevar
     * @param {string} datosPedido.clienteLlevar - Nombre del cliente
     * @param {string} datosPedido.usuario - Usuario que registra el pedido
     * @param {string} [datosPedido.tipoServicio='llevar'] - Tipo de servicio
     * @returns {Promise} - Promesa con los datos del pedido creado
     */
    crearPedidoLlevar: async (datosPedido) => {
      try {
        // Asegurarse de que el tipo de servicio sea 'llevar'
        const pedidoData = {
          ...datosPedido,
          tipo: datosPedido.tipoServicio || 'llevar',
          estado: 'abierto',
          fechaCreacion: new Date().toISOString()
        };

        const response = await apiClient.post('', {
          tipo: 'crearPedidoLlevar',
          datosPedido: pedidoData
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al crear pedido para llevar');
        }
      } catch (error) {
        console.error('Error al crear pedido para llevar:', error);
        throw error;
      }
    },

    /**
     * Actualizar un pedido para llevar existente
     * @param {Object} datosPedido - Datos actualizados del pedido
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    actualizarPedidoLlevar: async (datosPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarPedidoLlevar',
          datosPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar el pedido para llevar');
        }
      } catch (error) {
        console.error(`Error al actualizar pedido para llevar ${datosPedido.id}:`, error);
        throw error;
      }
    },

    /**
     * Actualizar precios de productos con promoción de mesa
     * @param {number} idPedido - ID del pedido
     * @param {boolean} activarPromocion - true para aplicar descuento, false para restaurar precio original
     * @param {Array} productosConDescuento - Array de productos con nuevos precios (solo cuando activarPromocion=true)
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    actualizarPreciosConPromocion: async (idPedido, activarPromocion, productosConDescuento = []) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarPreciosConPromocion',
          idPedido,
          activarPromocion,
          productosConDescuento
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar precios con promoción');
        }
      } catch (error) {
        console.error(`Error al actualizar precios con promoción del pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Añadir un producto a un pedido para llevar existente
     * @param {number} idPedido - ID del pedido
     * @param {Object} producto - Datos del producto a añadir
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    agregarProductoLlevar: async (idPedido, producto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'agregarProductoPedidoLlevar',
          idPedido,
          producto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al agregar producto al pedido para llevar');
        }
      } catch (error) {
        console.error(`Error al agregar producto al pedido para llevar ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Obtener un pedido para llevar por ID
     * @param {number} idPedido - ID del pedido para llevar
     * @returns {Promise} - Promesa con los datos del pedido
     */
    getPedidoLlevar: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidoLlevar',
          idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener pedido para llevar');
        }
      } catch (error) {
        console.error(`Error al obtener pedido para llevar ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Obtener todos los pedidos para llevar según su estado
     * @param {string} estado - Estado de los pedidos ('abiertos', 'pendientes', 'pagados')
     * @returns {Promise} - Promesa con la lista de pedidos para llevar
     */
    getPedidosLlevar: async (estado = 'abiertos') => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidosLlevar',
          estado
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener pedidos para llevar');
        }
      } catch (error) {
        console.error(`Error al obtener pedidos para llevar en estado ${estado}:`, error);
        throw error;
      }
    },

    cerrarPedidoLlevar: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cerrarPedidoLlevar',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al cerrar pedido para llevar');
        }
      } catch (error) {
        console.error('Error al cerrar pedido para llevar:', error);
        throw error;
      }
    },

    abrirCuentaLlevar: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'abrirCuentaLlevar',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al abrir cuenta para llevar');
        }
      } catch (error) {
        console.error('Error al abrir cuenta para llevar:', error);
        throw error;
      }
    },

    finalizarPedidoLlevar: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'finalizarPedidoLlevar',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al finalizar pedido para llevar');
        }
      } catch (error) {
        console.error('Error al finalizar pedido para llevar:', error);
        throw error;
      }
    },

    // ============================================
    // MÉTODOS ESPECÍFICOS PARA PEDIDOS A DOMICILIO
    // ============================================

    /**
     * Crear un nuevo pedido a domicilio
     * @param {Object} pedidoData - Datos del pedido a domicilio
     * @param {string} pedidoData.clienteDomicilio - Información completa del cliente
     * @param {string} pedidoData.nombreCliente - Nombre del cliente
     * @param {string} pedidoData.telefono - Teléfono del cliente
     * @param {string} pedidoData.direccion - Dirección de entrega
     * @param {string} pedidoData.repartidor - Nombre del repartidor (opcional)
     * @param {Array} pedidoData.detalles - Productos del pedido (opcional)
     * @returns {Promise} - Promesa con los datos del pedido creado
     */
    crearPedidoDomicilio: async (pedidoData) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'crearPedidoDomicilio',
          datosPedido: {
            clienteDomicilio: pedidoData.clienteDomicilio || pedidoData.nombreCliente,
            nombreCliente: pedidoData.nombreCliente,
            telefono: pedidoData.telefono,
            direccion: pedidoData.direccion,
            fraccionamiento: pedidoData.fraccionamiento || '',
            referencia: pedidoData.referencia || '',
            usuario: pedidoData.usuario || 'Sistema 1235',
            detalles: pedidoData.detalles || []
          }
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al crear pedido a domicilio');
        }
      } catch (error) {
        console.error('Error al crear pedido a domicilio:', error);
        throw error;
      }
    },

    /**
     * Obtener un pedido a domicilio por ID
     * @param {number} idPedido - ID del pedido
     * @returns {Promise} - Promesa con los datos del pedido
     */
    getPedidoDomicilio: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidoDomicilio',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener pedido a domicilio');
        }
      } catch (error) {
        console.error('Error al obtener pedido a domicilio:', error);
        throw error;
      }
    },

    /**
     * Actualizar un pedido a domicilio
     * @param {number} idPedido - ID del pedido
     * @param {Object} datosActualizacion - Datos a actualizar
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    actualizarPedidoDomicilio: async (idPedido, datosActualizacion) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarPedidoDomicilio',
          idPedido: idPedido,
          datosActualizacion: datosActualizacion
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar pedido a domicilio');
        }
      } catch (error) {
        console.error('Error al actualizar pedido a domicilio:', error);
        throw error;
      }
    },

    /**
     * Agregar un producto a un pedido a domicilio
     * @param {number} idPedido - ID del pedido
     * @param {Object} producto - Datos del producto a agregar
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    agregarProductoPedidoDomicilio: async (idPedido, producto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'agregarProductoPedidoDomicilio',
          idPedido: idPedido,
          producto: producto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al agregar producto al pedido a domicilio');
        }
      } catch (error) {
        console.error('Error al agregar producto al pedido a domicilio:', error);
        throw error;
      }
    },

    /**
     * Cerrar un pedido a domicilio
     * @param {number} idPedido - ID del pedido
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    cerrarPedidoDomicilio: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cerrarPedidoDomicilio',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al cerrar pedido a domicilio');
        }
      } catch (error) {
        console.error('Error al cerrar pedido a domicilio:', error);
        throw error;
      }
    },

    /**
     * Abrir cuenta de un pedido a domicilio
     * @param {number} idPedido - ID del pedido
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    abrirCuentaDomicilio: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'abrirCuentaDomicilio',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al abrir cuenta a domicilio');
        }
      } catch (error) {
        console.error('Error al abrir cuenta a domicilio:', error);
        throw error;
      }
    },

    /**
     * Finalizar un pedido a domicilio
     * @param {number} idPedido - ID del pedido
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    finalizarPedidoDomicilio: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'finalizarPedidoDomicilio',
          idPedido: idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al finalizar pedido a domicilio');
        }
      } catch (error) {
        console.error('Error al finalizar pedido a domicilio:', error);
        throw error;
      }
    },

    /**
     * Buscar cliente por número de teléfono
     * @param {string} telefono - Número de teléfono a buscar
     * @returns {Promise} - Promesa con los datos del cliente si existe
     */
    buscarClientePorTelefono: async (telefono) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'buscarClientePorTelefono',
          telefono: telefono
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al buscar cliente por teléfono');
        }
      } catch (error) {
        console.error('Error al buscar cliente por teléfono:', error);
        throw error;
      }
    },

    /**
     * Marcar productos como impresos después de enviar comandas
     * @param {number} idPedido - ID del pedido
     * @param {Array} idsProductos - IDs de los productos a marcar como impresos
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    marcarProductosImpresos: async (idPedido, idsProductos) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'marcarProductosImpresos',
          idPedido: idPedido,
          idsProductos: idsProductos
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al marcar productos como impresos');
        }
      } catch (error) {
        console.error('Error al marcar productos como impresos:', error);
        throw error;
      }
    },

    /**
     * Guardar o actualizar cliente en la base de datos
     * @param {Object} datosCliente - Datos del cliente
     * @param {string} datosCliente.nombre - Nombre del cliente
     * @param {string} datosCliente.telefono - Teléfono del cliente
     * @param {string} datosCliente.direccion - Dirección completa del cliente
     * @param {string} [datosCliente.referencia] - Referencia opcional
     * @returns {Promise} - Promesa con el resultado del guardado
     */
    guardarCliente: async (datosCliente) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'guardarCliente',
          ...datosCliente
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al guardar cliente');
        }
      } catch (error) {
        console.error('Error al guardar cliente:', error);
        throw error;
      }
    },

    // Añadir este método a la sección de pedidos en tu archivo API.js

    /**
     * Cambiar un pedido de una mesa a otra
     * @param {Object} datos - Datos necesarios para el cambio de mesa
     * @param {number} datos.idPedido - ID del pedido
     * @param {number} datos.idMesaOriginal - ID de la mesa original
     * @param {number} datos.idMesaDestino - ID de la mesa destino
     * @param {number} datos.numeroMesaDestino - Número de la mesa destino
     * @param {string} datos.nombreMesaDestino - Nombre de la mesa destino
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    cambiarMesa: async (datos) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cambiarMesaPedido',
          idPedido: datos.idPedido,
          idMesaOriginal: datos.idMesaOriginal,
          idMesaDestino: datos.idMesaDestino,
          numeroMesaDestino: datos.numeroMesaDestino,
          nombreMesaDestino: datos.nombreMesaDestino
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al cambiar de mesa');
        }
      } catch (error) {
        console.error('Error en cambiarMesa:', error);
        throw error;
      }
    },
    /**
     * Verificar si existe un pedido activo en una mesa
     * @param {number} idMesa - ID de la mesa a verificar
     * @returns {Promise} - Promesa con información del pedido si existe
     */
    verificarPedidoEnMesa: async (idMesa) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'verificarPedidoEnMesa',
          idMesa
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al verificar pedido en mesa');
        }
      } catch (error) {
        console.error(`Error al verificar pedido en mesa ${idMesa}:`, error);
        throw error;
      }
    },

    /**
     * Crear un nuevo pedido
     * @param {Object} datosPedido - Datos del pedido a crear
     * @returns {Promise} - Promesa con los datos del pedido creado
     */
    crearPedido: async (datosPedido) => {
      try {
        console.log("datosPedido", datosPedido)
        const response = await apiClient.post('', {
          tipo: 'crearPedido',
          datosPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al crear pedido');
        }
      } catch (error) {
        console.error('Error al crear pedido:', error);
        throw error;
      }
    },

    /**
     * Obtener detalles completos de un pedido
     * @param {number} idPedido - ID del pedido
     * @returns {Promise} - Promesa con los datos completos del pedido
     */
    getPedido: async (idPedido) => {
      try {
        console.log('🔍 Solicitando pedido al servidor, ID:', idPedido);

        const response = await apiClient.post('', {
          tipo: 'getPedido',
          idPedido
        });

        console.log('📥 Respuesta getPedido del servidor:', response.data);
        if (response.data.success && response.data.data) {
          console.log('👥 Número de personas en respuesta:', response.data.data.numeroPersonas);
        }

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener el pedido');
        }
      } catch (error) {
        console.error(`❌ Error al obtener pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Actualizar un pedido existente
     * @param {Object} datosPedido - Datos actualizados del pedido
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    actualizarPedido: async (datosPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarPedido',
          datosPedido
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar el pedido');
        }
      } catch (error) {
        console.error(`Error al actualizar pedido ${datosPedido.idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Cambiar solo el número de personas de un pedido
     * @param {number} idPedido - ID del pedido
     * @param {number} numeroPersonas - Nuevo número de personas
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    cambiarNumeroPersonas: async (idPedido, numeroPersonas) => {
      try {
        console.log('📡 Enviando cambio de personas al servidor:');
        console.log('  - idPedido:', idPedido);
        console.log('  - numeroPersonas:', numeroPersonas);
        console.log('  - tipo de numeroPersonas:', typeof numeroPersonas);

        const response = await apiClient.post('', {
          tipo: 'cambiarNumeroPersonasPedido',
          idPedido,
          numeroPersonas
        });

        console.log('📥 Respuesta del servidor:', response.data);

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al cambiar número de personas');
        }
      } catch (error) {
        console.error(`❌ Error al cambiar número de personas del pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Cambiar solo el mesero de un pedido
     * @param {number} idPedido - ID del pedido
     * @param {string} mesero - Nombre del nuevo mesero
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    cambiarMesero: async (idPedido, mesero) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cambiarMeseroPedido',
          idPedido,
          mesero
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al cambiar mesero');
        }
      } catch (error) {
        console.error(`Error al cambiar mesero del pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Añadir un producto a un pedido existente
     * @param {number} idPedido - ID del pedido
     * @param {Object} producto - Datos del producto a añadir
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    agregarProducto: async (idPedido, producto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'agregarProductoPedido',
          idPedido,
          producto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al agregar producto al pedido');
        }
      } catch (error) {
        console.error(`Error al agregar producto al pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Guardar productos nuevos sin cambiar el estado del pedido (para botón Regresar)
     * @param {number} idPedido - ID del pedido
     * @param {Array} productos - Array de productos a guardar
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    guardarProductosNuevos: async (idPedido, productos) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'guardarProductosNuevos',
          idPedido,
          productos
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al guardar productos nuevos');
        }
      } catch (error) {
        console.error(`Error al guardar productos nuevos en pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Eliminar un producto de un pedido
     * @param {number} idPedido - ID del pedido
     * @param {number} idPedidoDetalle - ID del detalle a eliminar
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    eliminarProducto: async (idPedido, idPedidoDetalle) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'eliminarProductoPedido',
          idPedido,
          idPedidoDetalle
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al eliminar producto del pedido');
        }
      } catch (error) {
        console.error(`Error al eliminar producto del pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Actualizar la cantidad de un producto en un pedido
     * @param {number} idPedido - ID del pedido
     * @param {number} idPedidoDetalle - ID del detalle a actualizar
     * @param {number} cantidad - Nueva cantidad del producto
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    actualizarCantidad: async (idPedido, idPedidoDetalle, cantidad) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarCantidadProducto',
          idPedido,
          idPedidoDetalle,
          cantidad
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar cantidad del producto');
        }
      } catch (error) {
        console.error(`Error al actualizar cantidad en pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Finalizar un pedido para proceder al pago
     * @param {number} idPedido - ID del pedido a finalizar
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    cerrarPedido: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cerrarPedido',
          idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al cerrar el pedido');
        }
      } catch (error) {
        console.error('Error al cerrar pedido:', error);
        throw error;
      }
    },

    abrirCuenta: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'abrirCuenta',
          idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al abrir la cuenta');
        }
      } catch (error) {
        console.error('Error al abrir cuenta:', error);
        throw error;
      }
    },

    finalizarPedido: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'finalizarPedido',
          idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al finalizar el pedido');
        }
      } catch (error) {
        console.error(`Error al finalizar pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Guardar código de facturación en un pedido
     * @param {Object} data - Datos del código de facturación
     * @param {number} data.idPedido - ID del pedido
     * @param {string} data.codigoFacturacion - Código de facturación generado
     * @param {string} data.fechaGeneracion - Fecha de generación del código
     * @param {string} data.tipoPedido - Tipo de pedido
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    guardarCodigoFacturacion: async ({ idPedido, codigoFacturacion, fechaGeneracion, tipoPedido }) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarCodigoPedidoFactura',
          idPedido,
          codigopedidofactura: codigoFacturacion,
          fechaGeneracion,
          tipoPedido
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al guardar código de facturación');
        }
      } catch (error) {
        console.error(`Error al guardar código de facturación para pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Marcar pedido como liquidado
     * @param {Object} data - Datos para marcar como liquidado
     * @param {number} data.idPedido - ID del pedido
     * @param {number} data.liquidado - Valor de liquidado (1 = liquidado)
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    marcarComoLiquidado: async ({ idPedido, liquidado }) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'marcarPedidoComoLiquidado',
          idPedido,
          liquidado
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al marcar pedido como liquidado');
        }
      } catch (error) {
        console.error(`Error al marcar pedido ${idPedido} como liquidado:`, error);
        throw error;
      }
    },

    /**
     * Cancelar un pedido
     * @param {number} idPedido - ID del pedido a cancelar
     * @param {string} motivo - Motivo de la cancelación
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    cancelarPedido: async (idPedido, motivo) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'cancelarPedido',
          idPedido,
          motivo
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al cancelar el pedido');
        }
      } catch (error) {
        console.error(`Error al cancelar pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Separar cuenta de un pedido
     * @param {Object} datosSeparacion - Datos para la separación de cuenta
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    separarCuenta: async (datosSeparacion) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'separarCuenta',
          datosSeparacion
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al separar la cuenta');
        }
      } catch (error) {
        console.error('Error al separar cuenta:', error);
        throw error;
      }
    },

    /**
     * Obtener todos los pedidos activos
     * @param {string} tipoFiltro - Filtro por tipo de pedido (mesa, domicilio, llevar)
     * @returns {Promise} - Promesa con la lista de pedidos activos
     */
    getPedidosActivos: async (tipoFiltro = '', activeStatusTab = '') => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPedidosActivos',
          tipoFiltro,
          activeStatusTab
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener pedidos activos');
        }
      } catch (error) {
        console.error('Error al obtener pedidos activos:', error);
        throw error;
      }
    },

    /**
     * Agregar observaciones a un producto en un pedido
     * @param {number} idPedido - ID del pedido
     * @param {number} idPedidoDetalle - ID del detalle
     * @param {Array} observaciones - Lista de observaciones
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    agregarObservaciones: async (idPedido, idPedidoDetalle, observaciones) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'agregarObservacionesProducto',
          idPedido,
          idPedidoDetalle,
          observaciones
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al agregar observaciones');
        }
      } catch (error) {
        console.error(`Error al agregar observaciones al producto en pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Aplicar promociones a un pedido
     * @param {number} idPedido - ID del pedido
     * @param {Array} promociones - Lista de promociones a aplicar
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    aplicarPromociones: async (idPedido, promociones) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'aplicarPromocionesAPedido',
          idPedido,
          promociones
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al aplicar promociones al pedido');
        }
      } catch (error) {
        console.error(`Error al aplicar promociones al pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Procesar pago de un pedido
     * @param {Object} datosPago - Datos del pago
     * @param {number} datosPago.idPedido - ID del pedido
     * @param {Array} datosPago.formasPago - Array con las formas de pago
     * @param {number} datosPago.propina - Monto de propina
     * @param {string} datosPago.usuario - Usuario que procesa el pago
     * @returns {Promise} - Promesa con el resultado del pago
     */
    procesarPago: async (datosPago) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'procesarPago',
          ...datosPago
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al procesar el pago');
        }
      } catch (error) {
        console.error('Error al procesar pago:', error);
        throw error;
      }
    },

    /**
     * Actualizar cambio entregado en un pago
     */
    actualizarCambioPago: async (idPedido, cambioEntregado) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'actualizarCambioPago',
          idPedido,
          cambioEntregado
        });

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al actualizar cambio');
        }
      } catch (error) {
        console.error('Error al actualizar cambio:', error);
        throw error;
      }
    },

    /**
     * Obtener información de pago de un pedido
     * @param {number} idPedido - ID del pedido
     * @returns {Promise} - Promesa con la información del pago
     */
    getPagoPedido: async (idPedido) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPagoPedido',
          idPedido
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener información de pago');
        }
      } catch (error) {
        console.error(`Error al obtener pago del pedido ${idPedido}:`, error);
        throw error;
      }
    },

    /**
     * Revertir un pago (cancelar)
     * @param {Object} datos - Datos para revertir el pago
     * @param {number} datos.idPago - ID del pago a revertir
     * @param {string} datos.motivo - Motivo de la reversión
     * @param {string} datos.usuario - Usuario que revierte
     * @returns {Promise} - Promesa con el resultado
     */
    revertirPago: async (datos) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'revertirPago',
          ...datos
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al revertir el pago');
        }
      } catch (error) {
        console.error('Error al revertir pago:', error);
        throw error;
      }
    },

    /**
     * Cambiar estado de un pedido a domicilio
     * @param {number} idPedido - ID del pedido
     * @param {string} nuevoStatus - Nuevo estado (finalizado, enviado, entregado)
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    cambiarEstadoPedido: async (idPedido, nuevoStatus) => {
      try {
        let endpoint;
        let requestData = { idPedido };

        // Mapear el status a los endpoints específicos disponibles
        switch (nuevoStatus) {
          case 'finalizado':
            endpoint = 'finalizarPedidoDomicilio';
            break;
          case 'enviado':
            // Por ahora usar un endpoint genérico para enviado
            endpoint = 'cambiarStatusPedidoDomicilio';
            requestData.nuevoStatus = 'enviado';
            break;
          case 'entregado':
            endpoint = 'cerrarPedidoDomicilio';
            break;
          default:
            throw new Error(`Estado '${nuevoStatus}' no soportado`);
        }

        const response = await apiClient.post('', {
          tipo: endpoint,
          ...requestData
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || `Error al cambiar estado a ${nuevoStatus}`);
        }
      } catch (error) {
        console.error(`Error al cambiar estado del pedido a ${nuevoStatus}:`, error);
        throw error;
      }
    }
  },

  categorias: {
    /**
     * Obtener todas las categorías
     * @returns {Promise} - Promesa con la lista de categorías
     */
    getAll: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getCategorias'
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener categorías');
        }
      } catch (error) {
        console.error('Error al obtener categorías:', error);

        // Datos de demostración en caso de error
        return [
          { idCategoria: 1, nombre: "BEBIDAS", colorFondo: "#2a2a2a", colorLetra: "#ffffff" },
          { idCategoria: 2, nombre: "ENTRADAS", colorFondo: "#2a2a2a", colorLetra: "#ffffff" },
          { idCategoria: 3, nombre: "PLATOS FUERTES", colorFondo: "#2a2a2a", colorLetra: "#ffffff" },
          { idCategoria: 4, nombre: "POSTRES", colorFondo: "#2a2a2a", colorLetra: "#ffffff" },
          { idCategoria: 5, nombre: "PROMOCIONES", colorFondo: "#2a2a2a", colorLetra: "#ffffff" }
        ];
      }
    },

    /**
     * Obtener una categoría por ID
     * @param {number} idCategoria - ID de la categoría
     * @returns {Promise} - Promesa con los datos de la categoría
     */
    getById: async (idCategoria) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getCategoria',
          idCategoria
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener la categoría');
        }
      } catch (error) {
        console.error(`Error al obtener categoría ${idCategoria}:`, error);
        throw error;
      }
    }
  },
  meseros: {
    /**
     * Obtener todos los meseros
     * @returns {Promise} - Promesa con la lista de meseros
     */
    getAll: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getMeseros'
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener meseros');
        }
      } catch (error) {
        console.error('Error al obtener meseros:', error);
        throw error;
      }
    },

    /**
     * Obtener un mesero por ID
     * @param {number} idMesero - ID del mesero a obtener
     * @returns {Promise} - Promesa con los datos del mesero
     */
    getById: async (idMesero) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getMesero',
          idMesero
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener el mesero');
        }
      } catch (error) {
        console.error(`Error al obtener mesero con ID ${idMesero}:`, error);
        throw error;
      }
    }
  },
  productos: {
    // Este código debe ir dentro de tu archivo utils/api.js
    // Agrega esta función al objeto API.productos

    /**
     * Buscar productos por ingrediente
     * @param {string} termino - Ingrediente a buscar
     * @param {boolean} excluir - true para buscar sin este ingrediente, false para buscar con el ingrediente
     * @returns {Promise} - Promesa con la lista de productos encontrados
     */
    buscarPorIngrediente: async (termino, excluir = false) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'buscarPlatillosPorIngrediente',
          termino,
          excluir
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || `Error al buscar platillos ${excluir ? 'sin' : 'con'} ingrediente "${termino}"`);
        }
      } catch (error) {
        console.error(`Error al buscar platillos por ingrediente "${termino}":`, error);

        // En caso de error, devolver array vacío
        return [];
      }
    },
    getPorCategoria: async (idCategoria) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPorCategoria',
          idCategoria
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener productos');
        }
      } catch (error) {
        console.error(`Error al obtener productos de la categoría ${idCategoria}:`, error);
        throw error;
      }
    },

    /**
     * Obtener ingredientes de un producto específico
     * @param {number} idProducto - ID del producto
     * @returns {Promise} - Promesa con la lista de ingredientes del producto
     */
    getIngredientesProducto: async (idProducto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getIngredientesProducto',
          idProducto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener ingredientes');
        }
      } catch (error) {
        console.error(`Error al obtener ingredientes del producto ${idProducto}:`, error);
        throw error;
      }
    },


    /**
     * Obtener un producto por ID
     * @param {number} idProducto - ID del producto
     * @returns {Promise} - Promesa con los datos del producto
     */
    getById: async (idProducto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPlatillo',
          idPlatillo: idProducto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener el producto');
        }
      } catch (error) {
        console.error(`Error al obtener producto ${idProducto}:`, error);
        throw error;
      }
    },

    /**
     * Buscar productos por nombre
     * @param {string} termino - Término de búsqueda
     * @returns {Promise} - Promesa con la lista de productos encontrados
     */
    buscar: async (termino) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'buscarPlatillos',
          termino
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al buscar productos');
        }
      } catch (error) {
        console.error(`Error al buscar productos con término "${termino}":`, error);
        throw error;
      }
    },

    /**
     * Verificar disponibilidad de un producto
     * @param {number} idProducto - ID del producto
     * @param {number} cantidad - Cantidad a verificar
     * @returns {Promise} - Promesa con información de disponibilidad
     */
    verificarDisponibilidad: async (idProducto, cantidad = 1) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'verificarDisponibilidadPlatillo',
          idPlatillo: idProducto,
          cantidad
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al verificar disponibilidad');
        }
      } catch (error) {
        console.error(`Error al verificar disponibilidad del producto ${idProducto}:`, error);

        // En caso de error, asumir que hay disponibilidad
        return {
          disponible: true,
          cantidad: 999,
          mensaje: "Disponibilidad simulada"
        };
      }
    }
  },
  promociones: {
    /**
     * Obtener detalles específicos de una promoción
     * @param {number} idPromocion - ID de la promoción
     * @returns {Promise} - Promesa con los detalles de la promoción
     */
    getDetallePromocion: async (idPromocion) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getDetallePromocion',
          idPromocion
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener detalle de promoción');
        }
      } catch (error) {
        console.error(`Error al obtener detalle de promoción ${idPromocion}:`, error);
        return {
          productosCobrar: [],
          productosRegalar: []
        };
      }
    },

    /**
     * Obtener productos asociados a una promoción tipo 3 (regalo/combo)
     * @param {number} idPromocion - ID de la promoción
     * @returns {Promise} - Promesa con los productos de la promoción (a cobrar y regalar)
     */
    getProductosPromocionRegalo: async (idPromocion) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getProductosPromocionRegalo',
          idPromocion
        });

        if (response.data.success) {
          return {
            productosCobrar: response.data.data.productosCobrar || [],
            productosRegalar: response.data.data.productosRegalar || []
          };
        } else {
          throw new Error(response.data.message || `Error al obtener productos de promoción ${idPromocion}`);
        }
      } catch (error) {
        console.error(`Error al obtener productos de regalo para promoción ${idPromocion}:`, error);
        // Devolver arrays vacíos en caso de error
        return {
          productosCobrar: [],
          productosRegalar: []
        };
      }
    },

    /**
     * Obtener todas las promociones activas
     * @returns {Promise} - Promesa con la lista de promociones activas
     */
    getActivas: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPromocionesActivas'
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener promociones activas');
        }
      } catch (error) {
        console.error('Error al obtener promociones:', error);

        // Datos de demostración en caso de error
        return [
          {
            idPromocion: 1,
            nombre: "30% Descuento en Bebidas",
            tipo: "1", // Tipo 1: descuento de porcentaje
            activa: true,
            fechaInicio: "2023-01-01",
            fechaFin: "2025-12-31",
            aplicaProductos: [1, 2, 3], // IDs más simples para bebidas
            descuento: 0.30 // 30% de descuento
          },
          {
            idPromocion: 2,
            nombre: "2x1 en Hamburguesas",
            tipo: "2", // Tipo 2: compra X, lleva Y gratis
            activa: true,
            fechaInicio: "2023-01-01",
            fechaFin: "2025-12-31",
            aplicaProductos: [4, 5, 6], // IDs para hamburguesas
            cantidad1: 1, // Compra 1
            cantidad2: 1, // Lleva 1
            descuento: 1.0, // 100% de descuento en el segundo producto
            descripcion: "Compra 1 hamburguesa y lleva otra gratis - se cobra la más cara"
          },
          {
            idPromocion: 3,
            nombre: "3x2 en Papas Fritas",
            tipo: "2", // Tipo 2: compra X, lleva Y gratis
            activa: true,
            fechaInicio: "2023-01-01",
            fechaFin: "2025-12-31",
            aplicaProductos: [7, 8, 9], // IDs para papas
            cantidad1: 2, // Compra 2
            cantidad2: 1, // Lleva 1
            descuento: 1.0, // 100% de descuento en el tercer producto
            descripcion: "Compra 2 papas fritas y lleva 1 gratis"
          },
          {
            idPromocion: 4,
            nombre: "Combo Pizza + Bebida",
            tipo: "3", // Tipo 3: compra específicos, lleva específicos
            activa: true,
            fechaInicio: "2023-01-01",
            fechaFin: "2025-12-31",
            // Estos productos serán reemplazados por los que vengan de la API
            productosCobrar: [10, 11], // IDs para pizzas
            productosRegalar: [1, 2], // IDs para bebidas (gratis)
            descripcion: "Compra una pizza y lleva una bebida gratis"
          }
        ];
      }
    },

    /**
     * Obtener promociones por tipo
     * @param {string} tipo - Tipo de promoción (1, 2, 3)
     * @returns {Promise} - Promesa con la lista de promociones del tipo especificado
     */
    getPorTipo: async (tipo) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getPromocionesPorTipo',
          tipoPromocion: tipo
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || `Error al obtener promociones tipo ${tipo}`);
        }
      } catch (error) {
        console.error(`Error al obtener promociones tipo ${tipo}:`, error);
        throw error;
      }
    },

    /**
     * Aplicar promociones a un pedido
     * @param {number} idPedido - ID del pedido
     * @param {Array} promociones - Lista de promociones aplicadas
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    aplicarPromociones: async (idPedido, promociones) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'aplicarPromociones',
          idPedido,
          promociones
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al aplicar promociones');
        }
      } catch (error) {
        console.error('Error al aplicar promociones:', error);
        throw error;
      }
    },

    /**
     * Verificar promociones aplicables a un producto específico
     * @param {number} idProducto - ID del producto
     * @returns {Promise} - Promesa con la lista de promociones aplicables
     */
    verificarPromocionesProducto: async (idProducto) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'verificarPromocionesProducto',
          idProducto
        });

        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al verificar promociones del producto');
        }
      } catch (error) {
        console.error('Error al verificar promociones:', error);
        throw error;
      }
    }
  },
  // Código para agregar/actualizar en el objeto API (utils/api.js)

  // Impresoras
  impresoras: {
    /**
     * Obtiene todas las impresoras necesarias desde el endpoint dedicado
     * Versión corregida con mejor manejo de errores
     * @param {boolean} forzarActualizacion - Si debe forzar la actualización ignorando cache
     * @returns {Promise<Array>} - Lista de impresoras necesarias
     */
    /**
 * Obtiene todas las impresoras necesarias ÚNICAMENTE desde el endpoint dedicado
 * Versión optimizada para NUNCA depender de los productos
 * @param {boolean} forzarActualizacion - Si debe forzar la actualización ignorando cache
 * @returns {Promise<Array>} - Lista de impresoras necesarias
 */
    getImpresorasNecesarias: async (forzarActualizacion = false) => {
      try {
        const ahora = Date.now();

        // Evitar múltiples consultas simultáneas
        if (consultaEnProceso) {
          console.log("Consulta de impresoras en progreso, usando caché...");
          return impresorasNecesariasCache;
        }

        // Usar la caché si existe y no ha expirado, a menos que se fuerce la actualización
        if (!forzarActualizacion &&
          impresorasNecesariasCache.length > 0 &&
          ultimaConsultaEndpoint &&
          (ahora - ultimaConsultaEndpoint < tiempoCacheEndpoint)) {
          console.log("Usando caché de impresoras necesarias");
          return impresorasNecesariasCache;
        }

        console.log("Consultando endpoint de impresoras necesarias");
        consultaEnProceso = true;

        try {
          // ÚNICA FUENTE DE VERDAD: El endpoint dedicado para impresoras
          const response = await apiClient.post('', {
            tipo: 'getImpresorasNecesarias'
          });

          if (response.data.success) {
            // Validar que el resultado sea un array
            if (!Array.isArray(response.data.data)) {
              console.warn("getImpresorasNecesarias no devolvió un array:", response.data.data);
              // Si no es un array, devolver la caché existente o array vacío
              return impresorasNecesariasCache.length > 0 ? impresorasNecesariasCache : [];
            }

            // Actualizar caché y timestamp
            impresorasNecesariasCache = response.data.data;
            ultimaConsultaEndpoint = ahora;

            return response.data.data;
          } else {
            console.warn("Error en respuesta de getImpresorasNecesarias:", response.data.message);
            // En caso de error, devolver la caché existente o array vacío
            return impresorasNecesariasCache.length > 0 ? impresorasNecesariasCache : [];
          }
        } finally {
          consultaEnProceso = false;
        }
      } catch (error) {
        console.error("Error en getImpresorasNecesarias:", error);
        consultaEnProceso = false;
        // Devolver la caché existente o array vacío
        return impresorasNecesariasCache.length > 0 ? impresorasNecesariasCache : [];
      }
    },

    /**
     * Obtiene platillos con su impresora asignada de una categoría específica
     * Versión corregida con mejor manejo de errores
     * @param {number} idCategoria - ID de la categoría
     * @returns {Promise<Array>} - Lista de platillos con sus impresoras
     */
    getPlatillosConImpresora: async (idCategoria) => {
      try {
        // Validar que idCategoria sea un número o string no vacío
        if (!idCategoria && idCategoria !== 0) {
          console.warn("ID de categoría inválido en getPlatillosConImpresora");
          return [];
        }

        const response = await apiClient.post('', {
          tipo: 'getPlatillosConImpresora',
          idCategoria
        });

        if (response.data.success) {
          // Validar que el resultado sea un array
          if (!Array.isArray(response.data.data)) {
            console.warn("getPlatillosConImpresora no devolvió un array:", response.data.data);
            return [];
          }

          return response.data.data;
        } else {
          console.warn("Error en respuesta de getPlatillosConImpresora:", response.data.message);
          return [];
        }
      } catch (error) {
        console.error(`Error en getPlatillosConImpresora para categoría ${idCategoria}:`, error);
        return [];
      }
    },

    /**
     * Verifica el estado de una impresora específica
     * Versión corregida con mejor manejo de errores
     * @param {string} nombreImpresora - Nombre de la impresora a verificar
     * @returns {Promise<Object>} - Estado de la impresora
     */
    verificarEstadoImpresora: async (nombreImpresora) => {
      try {
        // Validar que nombreImpresora sea un string no vacío
        if (!nombreImpresora || typeof nombreImpresora !== 'string') {
          return {
            disponible: false,
            online: false,
            error: "Nombre de impresora inválido",
            mensaje: "El nombre de impresora proporcionado no es válido"
          };
        }

        const response = await apiClient.post('', {
          tipo: 'verificarEstadoImpresora',
          nombreImpresora
        });

        if (response.data.success) {
          // Asegurar que el resultado tenga la estructura esperada
          const estado = response.data.data || {};

          return {
            disponible: !!estado.disponible,
            online: !!estado.online,
            error: estado.error || null,
            mensaje: estado.mensaje || "Estado obtenido correctamente"
          };
        } else {
          return {
            disponible: false,
            online: false,
            error: response.data.message || "Error al verificar estado",
            mensaje: "No se pudo obtener el estado de la impresora"
          };
        }
      } catch (error) {
        console.error(`Error en verificarEstadoImpresora para ${nombreImpresora}:`, error);

        return {
          disponible: false,
          online: false,
          error: `Error de comunicación: ${error.message || "Desconocido"}`,
          mensaje: "Error al comunicarse con el servidor"
        };
      }
    },

    /**
     * Obtiene la configuración de impresoras del sistema
     * @returns {Promise<Object>} - Configuración de impresoras
     */
    getConfiguracionImpresoras: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getConfiguracionImpresoras'
        });

        if (response.data.success) {
          return response.data.data || {};
        } else {
          console.warn("Error en respuesta de getConfiguracionImpresoras:", response.data.message);
          return {};
        }
      } catch (error) {
        console.error("Error en getConfiguracionImpresoras:", error);
        return {};
      }
    }
  },
  // Impresion automatica desde app
  comandaAuto: {
    getPendientes: async () => {
      try {
        const response = await apiClient.post('', { tipo: 'getPendientesApp' });
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al obtener pendientes app');
        }
      } catch (error) {
        console.error('Error al obtener pendientes app:', error);
        throw error;
      }
    },
    marcarImpresos: async (ids) => {
      try {
        const response = await apiClient.post('', { tipo: 'marcarImpresosApp', ids });
        if (response.data.success) {
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Error al marcar impresos app');
        }
      } catch (error) {
        console.error('Error al marcar impresos app:', error);
        throw error;
      }
    }
  },

  biometrico: biometricoAPI,

  // Métodos para autenticación por huella digital
  fingerprintAuth: {
    /**
     * Obtener huellas registradas para autenticación de un usuario
     * @param {number} userId - ID del usuario
     * @returns {Promise<Object>} - Resultado con las huellas del usuario
     */
    getUserFingerprints: async (userId) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getUserAuthFingerprints',
          userId: userId
        });

        return response.data;
      } catch (error) {
        console.error('Error al obtener huellas de autenticación:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Guardar nueva huella para autenticación
     * @param {Object} data - Datos de la huella
     * @param {number} data.userId - ID del usuario
     * @param {string} data.fingerName - Nombre del dedo
     * @param {string} data.templateData - Datos del template de la huella
     * @returns {Promise<Object>} - Resultado del guardado
     */
    saveFingerprint: async (data) => {
      try {
        console.log('🔄 Enviando datos a API saveAuthFingerprint:', data);

        const requestData = {
          tipo: 'saveAuthFingerprint',
          ...data
        };

        console.log('📡 Request completo:', requestData);

        const response = await apiClient.post('', requestData);

        console.log('📡 Response de API:', response.data);
        return response.data;
      } catch (error) {
        console.error('❌ Error al guardar huella de autenticación:', error);
        console.error('❌ Response error:', error.response?.data);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Eliminar huella de autenticación
     * @param {Object} data - Datos para eliminar
     * @param {number} data.fingerprintId - ID de la huella
     * @param {number} data.userId - ID del usuario (opcional para verificación)
     * @returns {Promise<Object>} - Resultado de la eliminación
     */
    deleteFingerprint: async (data) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'deleteAuthFingerprint',
          ...data
        });

        return response.data;
      } catch (error) {
        console.error('Error al eliminar huella de autenticación:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Verificar si un usuario puede usar autenticación biométrica
     * @param {Object} data - Datos de verificación
     * @param {number} data.userId - ID del usuario
     * @param {string} data.action - Acción que requiere autenticación
     * @returns {Promise<Object>} - Resultado de la verificación
     */
    verifyAuthentication: async (data) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'verifyAuthFingerprint',
          ...data
        });

        return response.data;
      } catch (error) {
        console.error('Error en verificación de autenticación:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Confirmar autenticación exitosa
     * @param {Object} data - Datos de confirmación
     * @param {number} data.userId - ID del usuario
     * @param {string} data.action - Acción autenticada
     * @param {number} data.confidence - Nivel de confianza (opcional)
     * @returns {Promise<Object>} - Resultado de la confirmación
     */
    confirmAuthentication: async (data) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'confirmAuthentication',
          ...data
        });

        return response.data;
      } catch (error) {
        console.error('Error al confirmar autenticación:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Identificar usuario por huella digital
     * @param {Object} data - Datos de la huella
     * @param {string} data.templateData - Template de la huella capturada
     * @param {number} data.confidence - Nivel de confianza (opcional)
     * @returns {Promise<Object>} - Resultado con datos del usuario identificado
     */
    identifyUserByFingerprint: async (data) => {
      try {
        console.log('🔍 API.fingerprintAuth.identifyUserByFingerprint - datos recibidos:', data);
        console.log('🔍 API.fingerprintAuth.identifyUserByFingerprint - templateData type:', typeof data.templateData);
        console.log('🔍 API.fingerprintAuth.identifyUserByFingerprint - templateData length:', data.templateData ? data.templateData.length : 'undefined');

        const requestData = {
          tipo: 'identifyUserByFingerprint',
          ...data
        };

        console.log('📡 Enviando al backend:', requestData);

        const response = await apiClient.post('', requestData);

        console.log('📡 Respuesta del backend:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error al identificar usuario por huella:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Obtener historial de autenticaciones
     * @param {Object} data - Parámetros de consulta
     * @param {number} data.userId - ID del usuario
     * @param {number} data.limit - Límite de resultados (opcional)
     * @param {string} data.action - Filtro por acción (opcional)
     * @returns {Promise<Object>} - Historial de autenticaciones
     */
    getAuthHistory: async (data) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getAuthHistory',
          ...data
        });

        return response.data;
      } catch (error) {
        console.error('Error al obtener historial de autenticación:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Obtener estadísticas de autenticación
     * @param {Object} data - Parámetros de consulta
     * @param {number} data.userId - ID del usuario
     * @param {number} data.days - Días atrás para estadísticas (opcional)
     * @returns {Promise<Object>} - Estadísticas de autenticación
     */
    getAuthStats: async (data) => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getAuthStats',
          ...data
        });

        return response.data;
      } catch (error) {
        console.error('Error al obtener estadísticas de autenticación:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

  },

  // Configuración del sistema
  configuracion: {
    /**
     * Obtener código de sucursal
     * @returns {Promise<Object>} - Código de sucursal
     */
    getCodigoSucursal: async () => {
      try {
        const response = await apiClient.post('', {
          tipo: 'getCodigoSucursal'
        });

        return response.data;
      } catch (error) {
        console.error('Error al obtener código de sucursal:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    }
  },

  // Pagos
  pagos: {
    /**
     * Registrar un pago
     * @param {Object} data - Datos del pago
     * @param {number} data.idPedido - ID del pedido
     * @param {string} data.tipoPago - Tipo de pago (EFECTIVO, TARJETA, UBER, RAPPI, etc.)
     * @param {number} data.monto - Monto del pago
     * @param {string} data.fecha - Fecha del pago
     * @returns {Promise} - Promesa con el resultado de la operación
     */
    registrarPago: async (datosPago) => {
      try {
        // Si es un objeto completo de mesa (tiene pagoTotal), enviarlo todo
        // Si es un pago simple, usar el formato anterior
        const payload = {
          tipo: 'registrarPago',
          ...datosPago
        };

        const response = await apiClient.post('', payload);

        if (response.data.success) {
          return response.data;
        } else {
          throw new Error(response.data.message || 'Error al registrar pago');
        }
      } catch (error) {
        console.error(`Error al registrar pago para pedido ${datosPago.idPedido}:`, error);
        throw error;
      }
    }
  },

  // Cortes de Caja
  cortesCaja: {
    /**
     * Obtener información del último corte final
     * @returns {Promise<Object>} - Datos del último corte
     */
    obtenerUltimoCorte: async () => {
      try {
        const response = await apiClient.get('?tipo=ultimo_corte&action=ultimo_corte');
        return response.data;
      } catch (error) {
        console.error('Error al obtener último corte:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Obtener el siguiente número de corte
     * @returns {Promise<Object>} - Siguiente número de corte
     */
    obtenerSiguienteCorte: async () => {
      try {
        const response = await apiClient.get('?tipo=siguiente_corte&action=siguiente_corte');
        return response.data;
      } catch (error) {
        console.error('Error al obtener siguiente corte:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Realizar corte de caja (parcial o final)
     * @param {Object} data - Datos del corte
     * @param {string} data.tipo - Tipo de corte ('parcial' o 'final')
     * @param {Object} data.arqueo - Objeto con las denominaciones y cantidades
     * @param {Object} data.opciones - Opciones de impresión
     * @param {string} data.usuario - Usuario que realiza el corte
     * @returns {Promise<Object>} - Resultado del corte
     */
    realizarCorte: async (data) => {
      try {
        const payload = {
          tipo: 'realizar_corte', // Para el backend
          tipo_corte: data.tipo_corte,  // Para la función (parcial/final)
          arqueo: data.arqueo,
          opciones: data.opciones,
          usuario: data.usuario,
          fondo_caja: data.fondo_caja
        };

        console.log('Enviando payload:', payload);

        const response = await apiClient.post('', payload);
        return response.data;
      } catch (error) {
        console.error('Error al realizar corte:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Consultar cortes por fecha
     * @param {string} fecha - Fecha en formato Y-m-d
     * @returns {Promise<Object>} - Lista de cortes de la fecha
     */
    consultarCortesPorFecha: async (fecha) => {
      try {
        const response = await apiClient.get(`?tipo=consultar_fecha&action=consultar_fecha&fecha=${fecha}`);
        return response.data;
      } catch (error) {
        console.error('Error al consultar cortes por fecha:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    }
  },

  // Información del Negocio
  infoNegocio: {
    /**
     * Obtener la información del negocio
     * @returns {Promise<Object>} - Datos del negocio (nombre, dirección, RFC, etc.)
     */
    obtenerInfo: async () => {
      try {
        const response = await apiClient.get('?tipo=getInfoNegocio');
        return response.data;
      } catch (error) {
        console.error('Error al obtener información del negocio:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    },

    /**
     * Actualizar la información del negocio
     * @param {Object} data - Datos del negocio a actualizar
     * @param {string} data.nombre_negocio - Nombre del negocio
     * @param {string} data.nombre_sucursal - Nombre de la sucursal
     * @param {string} data.direccion_linea1 - Primera línea de dirección
     * @param {string} data.direccion_linea2 - Segunda línea de dirección
     * @param {string} data.razon_social - Razón social
     * @param {string} data.rfc - RFC del negocio
     * @param {string} data.telefono - Teléfono del negocio
     * @param {string} data.email - Email del negocio
     * @param {string} data.sitio_web - Sitio web del negocio
     * @param {string} data.logo_url - URL del logo (opcional)
     * @param {string} data.mensaje_ticket - Mensaje adicional para tickets
     * @returns {Promise<Object>} - Resultado de la actualización
     */
    actualizarInfo: async (data) => {
      try {
        const payload = {
          tipo: 'actualizarInfoNegocio',
          ...data
        };

        const response = await apiClient.post('', payload);
        return response.data;
      } catch (error) {
        console.error('Error al actualizar información del negocio:', error);
        return {
          success: false,
          message: 'Error de comunicación: ' + error.message
        };
      }
    }
  }
};

/**
 * Obtiene los productos que se cobran y los que se regalan para una promoción tipo 3
 * @param {number} idPromocion - ID de la promoción
 * @returns {Promise<Object>} - Objeto con arrays de IDs de productos que se cobran y que se regalan
 */



export default API;